import React, { useState, useEffect } from 'react'
import { OTPInput, SlotProps } from 'input-otp'
import LoadingButton from '../shared/LoadingButton'
import LoginMethodFrame, {
  LoginMethodContentsOnSubmitProps,
} from '../shared/LoginMethodFrame'
import TermsCheckbox from '../shared/TermsCheckbox'
import { preventDefaultAndValidate } from '../../helpers'
import {
  InjectedSigninMethodProps,
  withSigninMethodProps,
} from '../../context/withSigninMethodProps'
import { AuthMethodEmailOTP, AuthMethodEnum } from '../../types.d'
import { useRetryMethod } from '../../hooks/useAuthActions'
import { useAppSelector, selectSigninData } from '../../store/store'

const Slot = (props: SlotProps & { color: string }) => {
  return (
    <div
      className={
        `bg-${props.color || 'slate'}-100 ` +
        `${props.isActive ? 'border-2 border-gray-700' : 'border-gray-300 '} ` +
        'flex items-center justify-center text-center justify-center border ' +
        'border-gray-500 block h-12 w-12 sm:h-14 sm:w-14 rounded-lg text-2xl ' +
        'font-extrabold text-gray-900 sm:text-3xl'
      }
    >
      {props.char?.toUpperCase() ?? props.placeholderChar}
    </div>
  )
}

const FakeDash = () => (
  <div className="flex w-3 justify-center items-center">
    <div className="w-3 h-1 rounded-full bg-black" />
  </div>
)

const PureSigninEmailOTPContents = ({
  recaptchaPromise,
  recaptchaLoading,
  onSubmit,
  onRetry,
  email,
  color,
  startTimeout,
  requiresTermsAgreement,
  termsAgreed,
  onTermsAgreementChange,
}: LoginMethodContentsOnSubmitProps & {
  color: string
  onRetry: () => void
  startTimeout: number
  requiresTermsAgreement: boolean
  termsAgreed: boolean
  onTermsAgreementChange: (agreed: boolean) => void
}) => {
  const [cooldown, setCooldown] = useState(startTimeout)
  const [emailOtp, setEmailOtp] = useState<string>('')

  useEffect(() => {
    if (cooldown <= 0) return
    const interval = setInterval(() => {
      setCooldown((prev) => prev - 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [cooldown])

  const submitSignin = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    onSubmit(emailOtp)
  }

  return (
    <>
      <p className="text-gray-500 text-center">
        Check your email for a{' '}
        {color ? (
          <span className={`font-bold text-${color}-400`}>{color}&nbsp;</span>
        ) : (
          ''
        )}
        code from TidyHQ.
      </p>
      <input
        type="text"
        name="email"
        value={email}
        autoComplete="username"
        readOnly
        hidden
      />
      <OTPInput
        autoFocus
        pattern="^[0-9A-Za-z]+$"
        maxLength={6}
        inputMode="text"
        containerClassName="flex flex-row flex-wrap justify-center gap-1 md:gap-2 has-[:disabled]:opacity-30"
        pasteTransformer={(value) =>
          value
            .toUpperCase()
            .replace(/I/g, '1') // Crockford: I → 1
            .replace(/L/g, '1') // Crockford: L → 1
            .replace(/O/g, '0') // Crockford: O → 0
            .replace(/[^0-9A-Z]/g, '')
            .slice(0, 6)
        }
        value={emailOtp}
        onChange={(value) => {
          // Apply Crockford normalization on input
          const normalized = value
            .toUpperCase()
            .replace(/I/g, '1')
            .replace(/L/g, '1')
            .replace(/O/g, '0')
          setEmailOtp(normalized)
        }}
        data-lpignore="true" // It's email otp, so PW managers aren't helpful
        data-1p-ignore="true"
        render={({ slots }) => (
          <>
            <div className="flex flex-row flex-wrap justify-center gap-1">
              {slots.slice(0, 3).map((slot, idx) => (
                <Slot key={idx} {...slot} color={color} />
              ))}
            </div>

            <FakeDash />

            <div className="flex flex-row flex-wrap justify-center gap-1">
              {slots.slice(3).map((slot, idx) => (
                <Slot key={idx} {...slot} color={color} />
              ))}
            </div>
          </>
        )}
      />
      {requiresTermsAgreement && (
        <TermsCheckbox
          checked={termsAgreed}
          onChange={onTermsAgreementChange}
          tabIndex={emailOtp.length + 1}
        />
      )}
      <LoadingButton
        loading={recaptchaLoading}
        onClick={preventDefaultAndValidate(submitSignin)}
        tabIndex={emailOtp.length + (requiresTermsAgreement ? 2 : 1)}
        type="submit"
        className={`bg-${color || 'slate'}-600 hover:bg-${color || 'slate'}-700 w-full text-white focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center`}
      >
        Sign in
      </LoadingButton>
      <p className="text-center text-sm font-light text-gray-500">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault()
            if (cooldown <= 0) {
              onRetry()
              setCooldown(60) // start 60-second lockout
            }
          }}
          className={`font-medium ${
            cooldown > 0
              ? 'text-gray-400 cursor-not-allowed pointer-events-none'
              : 'text-primary-600 hover:underline'
          }`}
          aria-disabled={cooldown > 0}
        >
          {cooldown > 0
            ? `Request another code in ${cooldown}s`
            : 'Re-Request Code'}
        </a>
      </p>
    </>
  )
}

type PureSigninEmailOTPProps = {
  recaptcha?: boolean
  onRetry: () => void
  startTimeout?: number
} & InjectedSigninMethodProps

export const PureSigninEmailOTP = ({
  onSubmit,
  onRetry,
  email,
  authMethod,
  startTimeout,
  requiresTermsAgreement,
  termsAgreed,
  onTermsAgreementChange,
  ...props
}: PureSigninEmailOTPProps) => (
  <LoginMethodFrame
    title="Sign in with email"
    recoveryPrompt="Can't access your email?"
    {...{ email, ...props }}
  >
    <PureSigninEmailOTPContents
      {...{
        email,
        onSubmit,
        onRetry,
        startTimeout,
        requiresTermsAgreement,
        termsAgreed,
        onTermsAgreementChange,
      }}
      color={(authMethod as AuthMethodEmailOTP)?.color}
    />
  </LoginMethodFrame>
)

const BoundSigninEmailOTP = withSigninMethodProps(PureSigninEmailOTP)

export const SigninEmailOTP = () => {
  const retryMethod = useRetryMethod()
  const signinData = useAppSelector(selectSigninData)

  let timeout = 60

  if (
    signinData?.authMethod?.type == AuthMethodEnum.EMAIL_OTP &&
    (signinData.authMethod as AuthMethodEmailOTP).timeout
  ) {
    timeout = (signinData.authMethod as AuthMethodEmailOTP).timeout
  }

  return <BoundSigninEmailOTP onRetry={retryMethod} startTimeout={timeout} /> // TODO: Fix oncancel handling
}

export default SigninEmailOTP
